import React, { useState } from 'react';
import {
    Button, Form, FormField, Icon, Input, Message,
    Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow,
} from 'semantic-ui-react';
import { fetchWithAuth, getCSRFToken } from '../auth';

const API_BASE = `${process.env.REACT_APP_ICC_TOOL_API_HOST}vacation-limit-overrides/`;

const parseApiError = async (resp) => {
    try {
        const body = await resp.json();
        return body.detail || body.non_field_errors?.[0] || body.endDate?.[0] || body.startDate?.[0] || null;
    } catch {
        return null;
    }
};

export const VacationLimitOverrides = ({ lockedTeam, overrides, onOverrideAdded, onOverrideDeleted, loading = false }) => {
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [limitValue, setLimitValue] = useState('');
    const [formError, setFormError] = useState('');
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(null);

    const clearError = () => setFormError('');

    const handleAdd = async () => {
        if (!startDate || !endDate) {
            setFormError('Please select start and end dates.');
            return;
        }
        if (endDate < startDate) {
            setFormError('End date must be on or after the start date.');
            return;
        }
        const limit = limitValue === '' ? null : parseInt(limitValue, 10);
        if (limit !== null && (isNaN(limit) || limit < 1)) {
            setFormError('Slots available must be a positive integer, or leave blank for no limit.');
            return;
        }
        if (overrides.some(o => o.startDate <= endDate && o.endDate >= startDate)) {
            setFormError('This date range overlaps with an existing override. Remove it first.');
            return;
        }

        setFormError('');
        setSaving(true);
        try {
            const resp = await fetchWithAuth(API_BASE, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-CSRFToken': getCSRFToken() },
                credentials: 'include',
                body: JSON.stringify({ teamId: lockedTeam.id, startDate, endDate, limit }),
            });
            if (!resp.ok) {
                setFormError((await parseApiError(resp)) ?? 'Failed to save override. Please try again.');
                return;
            }
            onOverrideAdded(await resp.json());
            setStartDate('');
            setEndDate('');
            setLimitValue('');
        } catch {
            setFormError('An error occurred. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        setDeleting(id);
        try {
            await fetchWithAuth(`${API_BASE}${id}/`, {
                method: 'DELETE',
                headers: { 'X-CSRFToken': getCSRFToken() },
                credentials: 'include',
            });
            onOverrideDeleted(id);
        } finally {
            setDeleting(null);
        }
    };

    return (
        <div data-testid="vacation-limit-overrides" style={{ maxWidth: 600 }}>
            {loading && <p>Loading\u2026</p>}
            {formError && (
                <Message negative size="small">
                    <p>{formError}</p>
                </Message>
            )}
            <Form size="small">
                <Form.Group widths="equal">
                    <FormField>
                        <label>Start date</label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={e => { setStartDate(e.target.value); clearError(); }}
                            data-testid="override-start-date-input"
                        />
                    </FormField>
                    <FormField>
                        <label>End date</label>
                        <input
                            type="date"
                            value={endDate}
                            min={startDate || undefined}
                            onChange={e => { setEndDate(e.target.value); clearError(); }}
                            data-testid="override-end-date-input"
                        />
                    </FormField>
                    <FormField>
                        <label>Slots available (blank = no limit)</label>
                        <Input
                            type="number"
                            min="1"
                            placeholder="No limit"
                            value={limitValue}
                            onChange={(_, d) => { setLimitValue(d.value); clearError(); }}
                            data-testid="override-limit-input"
                        />
                    </FormField>
                    <FormField style={{ display: 'flex', alignItems: 'flex-end' }}>
                        <Button
                            primary
                            size="small"
                            loading={saving}
                            disabled={saving || loading}
                            onClick={handleAdd}
                            data-testid="override-add-btn"
                        >
                            <Icon name="plus" /> Add
                        </Button>
                    </FormField>
                </Form.Group>
            </Form>

            {overrides.length > 0 && (
                <Table compact size="small" style={{ marginTop: '0.5rem' }}>
                    <TableHeader>
                        <TableRow>
                            <TableHeaderCell>Start</TableHeaderCell>
                            <TableHeaderCell>End</TableHeaderCell>
                            <TableHeaderCell>Slots available</TableHeaderCell>
                            <TableHeaderCell />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {overrides.map(o => (
                            <TableRow key={o.id}>
                                <TableCell>{o.startDate}</TableCell>
                                <TableCell>{o.endDate}</TableCell>
                                <TableCell>{o.limit == null ? 'No limit' : o.limit}</TableCell>
                                <TableCell textAlign="right">
                                    <Button
                                        icon="trash"
                                        size="mini"
                                        negative
                                        loading={deleting === o.id}
                                        disabled={deleting === o.id}
                                        onClick={() => handleDelete(o.id)}
                                        data-testid={`override-delete-${o.id}`}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </div>
    );
};
